import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Spinner, SectionLabel, Pill } from "../components/Primitives";

// Simple equirectangular projection onto a 1000x500 viewBox.
const project = (lng, lat) => [((lng + 180) / 360) * 1000, ((90 - lat) / 180) * 500];

export default function Dispersion() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/dispersion").then((r) => setRoutes(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;

  return (
    <div data-testid="dispersion-page">
      <div className="border-b border-edge bg-ash">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <SectionLabel>The Scattering</SectionLabel>
          <h1 className="font-serif text-5xl font-semibold text-bone lg:text-6xl">The Dispersion Map</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted">
            An estimated 12.5 million Africans were forcibly carried across the Atlantic. Trace where they were
            taken from, where they were brought, what they built, and what they endured.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-12 md:px-12">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* MAP */}
          <div className="lg:col-span-7">
            <div className="overflow-hidden border border-edge bg-[#0d0c0b]">
              <svg viewBox="0 0 1000 500" className="h-auto w-full" data-testid="dispersion-svg">
                <defs>
                  <linearGradient id="arc" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#8C1C13" />
                    <stop offset="100%" stopColor="#D4AF37" />
                  </linearGradient>
                </defs>
                <rect width="1000" height="500" fill="#0d0c0b" />
                {/* faint lat/long grid */}
                {[...Array(9)].map((_, i) => (
                  <line key={`v${i}`} x1={(i + 1) * 100} y1="0" x2={(i + 1) * 100} y2="500" stroke="#1F1D1C" strokeWidth="1" />
                ))}
                {[...Array(4)].map((_, i) => (
                  <line key={`h${i}`} x1="0" y1={(i + 1) * 100} x2="1000" y2={(i + 1) * 100} stroke="#1F1D1C" strokeWidth="1" />
                ))}
                {routes.map((r, i) => {
                  const [ox, oy] = project(r.origin_coords[0], r.origin_coords[1]);
                  const [dx, dy] = project(r.dest_coords[0], r.dest_coords[1]);
                  const mx = (ox + dx) / 2;
                  const my = Math.min(oy, dy) - 80;
                  const isActive = i === active;
                  return (
                    <g key={i} onClick={() => setActive(i)} style={{ cursor: "pointer" }} data-testid={`map-route-${i}`}>
                      <path
                        d={`M ${ox} ${oy} Q ${mx} ${my} ${dx} ${dy}`}
                        fill="none"
                        stroke="url(#arc)"
                        strokeWidth={isActive ? 2.5 : 1}
                        opacity={isActive ? 1 : 0.35}
                      />
                      <circle cx={ox} cy={oy} r={isActive ? 6 : 4} fill="#D4AF37" opacity={isActive ? 1 : 0.5} />
                      <circle cx={dx} cy={dy} r={isActive ? 6 : 4} fill="#8C1C13" opacity={isActive ? 1 : 0.6} />
                    </g>
                  );
                })}
              </svg>
            </div>
            <div className="mt-3 flex gap-6 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
              <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-gold" /> Origin region</span>
              <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-blood" /> Destination</span>
            </div>
          </div>

          {/* DETAIL + LIST */}
          <div className="lg:col-span-5">
            {routes[active] && (
              <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <Pill tone="gold">{routes[active].peak_period}</Pill>
                <h2 className="mt-3 font-serif text-3xl text-bone">{routes[active].origin_region}</h2>
                <div className="my-3 flex items-center gap-2 text-gold">
                  <ArrowRight className="h-4 w-4" />
                  <span className="text-bone">{routes[active].destination}</span>
                </div>
                <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
                  Estimated people: {routes[active].est_people}
                </p>
                <div className="mt-5 border-l-2 border-forest bg-ash p-4">
                  <h4 className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7bbd7e]">Contributions</h4>
                  <p className="mt-1 text-sm leading-relaxed text-[#cfc9bd]">{routes[active].contributions}</p>
                </div>
                <div className="mt-3 border-l-2 border-blood bg-ash p-4">
                  <h4 className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#e07a72]">Oppression Faced</h4>
                  <p className="mt-1 text-sm leading-relaxed text-[#cfc9bd]">{routes[active].oppression}</p>
                </div>
              </motion.div>
            )}

            <div className="mt-6 space-y-2">
              {routes.map((r, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`route-item-${i}`}
                  className={`block w-full border px-4 py-3 text-left text-sm transition-colors ${
                    i === active ? "border-gold bg-ash text-bone" : "border-edge text-muted hover:border-gold/50"
                  }`}
                >
                  {r.origin_region} → {r.destination}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
